import React from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Rows3, Tag } from 'lucide-react'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { LifeSpaceColorIcon } from '@/src/renderer/components/icons/LifeSpaceColorIcon'
import type { SuggestKind } from '@/src/renderer/lib/titleParser'
import type { SuggestionGroup, SuggestionItem } from './hooks/useTitleSuggestions'
import './TitleSuggestions.scss'

export interface TitleSuggestionsProps {
  kind: SuggestKind
  /** Was getippt ist — für die Zeile „Kein Projekt „…““. */
  query: string
  groups: SuggestionGroup[]
  /** Stelle in der flachen Liste aller Einträge. */
  highlighted: number
  onHighlight: (index: number) => void
  onAccept: (index: number) => void
}

function ItemIcon({ item }: { item: SuggestionItem }) {
  switch (item.type) {
    case 'project': return <ProjectIcon icon={item.icon} iconColor={item.iconColor} size={16} />
    case 'section': return <Rows3 size={16} aria-hidden />
    case 'tag': return <Tag size={16} aria-hidden />
    case 'new-tag':
    case 'new-section': return <Plus size={16} aria-hidden />
  }
}

/**
 * **Vorschläge im Titel** (#234, Schritt C) — nur der Inhalt: Gruppen mit Kopf, Einträge, „neu anlegen“,
 * und eine stille Zeile, wenn nichts passt. Die Hülle (Popover am Zeichen) stellt `TaskEntry`; der Fokus
 * bleibt im Titel, darum nimmt kein Eintrag ihn an (`mousedown` verhindert).
 *
 * @example
 * <TitleSuggestions kind="project" query="gar" groups={groups} highlighted={0}
 *   onHighlight={setHighlighted} onAccept={accept} />
 */
export const TitleSuggestions: React.FC<TitleSuggestionsProps> = ({ kind, query, groups, highlighted, onHighlight, onAccept }) => {
  const { t } = useTranslation()
  let index = -1

  if (groups.length === 0) {
    const key = kind === 'project' ? 'titleRecognition.noProject' : 'titleRecognition.noSection'
    return <div className="title-suggestions__empty">{t(key, { query })}</div>
  }

  return (
    <div className="title-suggestions" role="listbox" aria-label={t(`titleRecognition.suggestions.${kind}`)}>
      {groups.map(group => (
        <div key={group.key} className="title-suggestions__group" role="group">
          {group.lifeSpace && (
            <div className="context-menu-content__label title-suggestions__label">
              <LifeSpaceColorIcon lifeSpace={group.lifeSpace} size={14} />
              {t(`lifeSpaces.${group.lifeSpace}`)}
            </div>
          )}
          {group.projectName && (
            <div className="context-menu-content__label title-suggestions__label">
              {t('titleRecognition.sectionsIn', { project: group.projectName })}
            </div>
          )}
          {group.items.map(item => {
            index += 1
            const at = index
            return (
              <div
                key={`${item.type}:${'id' in item ? item.id : item.name}`}
                role="option"
                aria-selected={at === highlighted}
                className="context-menu-content__item title-suggestions__item"
                data-highlighted={at === highlighted ? '' : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onHighlight(at)}
                onClick={() => onAccept(at)}
              >
                <span className="context-menu-content__item-icon"><ItemIcon item={item} /></span>
                <span className="title-suggestions__name">
                  {item.type === 'new-tag' ? t('titleRecognition.createTag', { name: item.name })
                    : item.type === 'new-section' ? t('titleRecognition.createSection', { name: item.name })
                    : item.name}
                </span>
                {item.type === 'project' && item.parentName && (
                  <span className="title-suggestions__parent">{item.parentName}</span>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
